import type { ThemeDefinition } from "./types";

/**
 * Single source of truth for retail storefront themes.
 *
 * Consumed by three parties:
 *  - the SPA (ThemeProvider / slots resolver) at runtime,
 *  - the worker (validate + inject palette, font stack, @font-face, preloads),
 *  - the manager's theme picker via a mirrored static list (apps cannot
 *    import from each other; ids are stable strings).
 *
 * This file must stay dependency-free (no React/DOM imports): the worker
 * bundles it into its own edge runtime.
 *
 * Adding a theme = one folder under src/themes/<id>/ + one entry here
 * + one card in the manager picker.
 */

const BRAND_DEFAULT_FONT_STACK =
  "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

export const THEMES = {
  default: {
    id: "default",
    label: "Default",
    font: null,
    fontStack: BRAND_DEFAULT_FONT_STACK,
    palette: {},
    themeColor: "#000000",
    backgroundColor: "#ffffff",
  },
  elo: {
    id: "elo",
    label: "Elo",
    font: null,
    fontStack: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    palette: {
      "--elo-yellow": "#f5b301",
    },
    themeColor: "#ffffff",
    backgroundColor: "#ffffff",
  },
  nike: {
    id: "nike",
    label: "Nike",
    font: "Anton",
    fontStack:
      "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontFaces: [
      {
        family: "Anton",
        src: "/themes/nike/fonts/anton-400.woff2",
        weight: "400",
      },
    ],
    fontPreloads: ["/themes/nike/fonts/anton-400.woff2"],
    palette: {
      "--nike-ink": "#111111",
      "--nike-green": "#007d48",
    },
    themeColor: "#111111",
    backgroundColor: "#ffffff",
  },
  puma: {
    id: "puma",
    label: "Puma",
    font: null,
    fontStack: BRAND_DEFAULT_FONT_STACK,
    palette: {
      "--theme-surface": "#f8f8f8",
      "--theme-accent": "#111111",
    },
    themeColor: "#111111",
    backgroundColor: "#f8f8f8",
  },
} as const satisfies Record<string, ThemeDefinition>;

export type ThemeId = keyof typeof THEMES;

export type { ThemeDefinition };

export const resolveTheme = (theme?: string | null): ThemeId =>
  theme && theme in THEMES ? (theme as ThemeId) : "default";

export const getTheme = (theme?: string | null): ThemeDefinition =>
  THEMES[resolveTheme(theme)];
